import { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { FilterAltOff, Search } from '@mui/icons-material';
import {
  CompactTable,
  CustomButton,
  CustomTextField,
  DecimalTextField,
  HorizontalForm,
  InfoTooltip,
} from '../../../common/components';
import { useTableSort } from '../../../common/hooks/useTableSort';
import { useDebouncedValue } from '../../../common/hooks/useDebouncedValue';
import { TABLE_INPUT_SX } from '../../../common/styles/tableInputs';
import { sortRows } from '../../../utils/sortRows';
import { consolidatedMaterialTotals } from '../utils/consolidatedMaterialTotals';
import { buildConsolidatedSupplyColumns } from '../utils/consolidatedSupplyColumns';
import { CONSOLIDATED_SUPPLY_TEXTS as TEXTS } from '../constants/consolidatedSupplyTexts';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
const ORIGINS_MIN_WIDTH = 800;
const TOTALS_MIN_WIDTH = 600;

/** Totales por material o cantidades editables de cada pedido, en vistas separadas. */
const ConsolidatedSupplyMaterials = ({
  rows,
  onQuantity,
  showOrigins = false,
  disabled,
  onAdjust,
}) => {
  const { sort, toggleSort, resetSort } = useTableSort();
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search).trim().toLowerCase();
  const visible = useMemo(
    () =>
      (showOrigins ? rows : consolidatedMaterialTotals(rows))
        .filter((row) =>
          [
            row.article_name,
            row.article_code,
            row.order_number,
            row.destination_label,
            row.warehouse_name,
          ]
            .join(' ')
            .toLowerCase()
            .includes(query)
        )
        .map((row) => ({ ...row, quantity_sort: Number(row.quantity || 0) })),
    [rows, showOrigins, query]
  );
  const renderQuantityInput = (row) => {
    const isInvalid = Number(row.quantity || 0) > row.available_quantity;
    return (
      <Tooltip title={isInvalid ? TEXTS.quantityError : ''}>
        <Box component="span" sx={{ display: 'flex', minWidth: 0 }}>
          <DecimalTextField
            value={row.quantity}
            decimals={3}
            disabled={disabled}
            size="small"
            sx={TABLE_INPUT_SX}
            inputProps={{
              style: { textAlign: 'right' },
              'aria-label': [TEXTS.quantity, row.article_name, row.order_number].join(' '),
            }}
            error={isInvalid}
            onChange={(event) => onQuantity(row.uuid_external_order_item, event.target.value)}
          />
        </Box>
      </Tooltip>
    );
  };
  const columns = buildConsolidatedSupplyColumns({ showOrigins, renderQuantityInput });
  return (
    <Box>
      <HorizontalForm
        withBorder={false}
        sx={{ px: 2, py: 1.5, gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <CustomTextField
          label={TEXTS.search}
          value={search}
          sx={{ flex: '1 1 240px', minWidth: 0, mt: 0 }}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: <Search sx={{ color: 'primary.main', mr: 1 }} /> }}
        />
        {!showOrigins && (
          <CustomButton variant="outlined" onClick={onAdjust} sx={{ height: 42, fontSize: 14 }}>
            {TEXTS.adjust}
          </CustomButton>
        )}
        <Tooltip title={TEXTS.clearFilter}>
          <IconButton
            color="primary"
            aria-label={TEXTS.clearFilter}
            onClick={() => {
              setSearch('');
              resetSort();
            }}
          >
            <FilterAltOff />
          </IconButton>
        </Tooltip>
        <InfoTooltip lines={[TEXTS.editHint]} />
      </HorizontalForm>
      <ClientPaginatedTable
        rows={sortRows(visible, sort)}
        resetKey={`${query}:${showOrigins}:${sort?.key}:${sort?.direction}`}
      >
        {(pageRows) => (
          <CompactTable
            columns={columns}
            rows={pageRows}
            getRowKey={(row) => row.uuid_external_order_item}
            sort={sort}
            onSort={toggleSort}
            emptyMessage={query ? TEXTS.noMatches : TEXTS.empty}
            minTableWidth={showOrigins ? ORIGINS_MIN_WIDTH : TOTALS_MIN_WIDTH}
          />
        )}
      </ClientPaginatedTable>
    </Box>
  );
};

export default ConsolidatedSupplyMaterials;
